<template lang="html">
  <div class="cw-change-password">
    <div class="card-image">
      <div class="hero is-small is-black">
        <div class="hero-body has-text-centered">
          <div class="title is-3">Change Password</div>
        </div>
      </div>
    </div>
    <div class="card-content">
      <section class="section">
        <div class="label font-light">Change Password For</div>
        <p class="is-size-4 is-pad-bottom-1">{{ userInfo.email }}</p>

        <!-- Password Change Form -->
        <form class="form" v-if="!showMFAPrompt">
          <div class="field">
            <div class="label font-light">Current Password</div>
            <div class="control has-icons-left">
              <span class="icon is-left">
                <i class="las la-2x la-key"></i>
              </span>
              <input
                type="password"
                class="input is-underlined is-medium"
                :class="{ 'is-danger': currentPasswordError !== null }"
                @input="$v.currentPassword.$touch()"
                @focus="$v.currentPassword.$touch()"
                placeholder="Current Password"
                v-model="currentPassword"
              />
              <p class="help" v-if="currentPasswordError">
                {{ currentPasswordError }}
              </p>
            </div>
          </div>
          <div class="field">
            <div class="label font-light">New Password</div>
            <div class="control has-icons-left">
              <span class="icon is-left">
                <i class="las la-2x la-key"></i>
              </span>
              <input
                type="password"
                class="input is-underlined is-medium"
                :class="{ 'is-danger': passwordError !== null }"
                @input="$v.password.$touch()"
                @focus="$v.password.$touch()"
                placeholder="New Password"
                v-model="password"
              />
              <p class="help" v-if="passwordError">{{ passwordError }}</p>
            </div>
          </div>
          <div class="field">
            <div class="control has-icons-left">
              <span class="icon is-left">
                <i class="las la-2x la-key"></i>
              </span>
              <input
                type="password"
                class="input is-underlined is-medium"
                :class="{ 'is-danger': confirmPasswordError !== null }"
                @input="$v.confirmPassword.$touch()"
                @focus="$v.confirmPassword.$touch()"
                placeholder="Confirm Password"
                v-model="confirmPassword"
              />
              <p class="help" v-if="confirmPasswordError">
                {{ confirmPasswordError }}
              </p>
            </div>
          </div>
          <div class="field is-grouped is-grouped-right">
            <div class="control">
              <button
                class="button is-medium is-primary is-rounded"
                type="button"
                name="button"
                @click="submit"
              >
                Change Password
              </button>
            </div>
          </div>
        </form>

        <!-- MFA Verification Form -->
        <div v-else>
          <cw-mfa-verification
            v-model="mfaCode"
            :phoneNumber="mfaPhoneNumber"
            :errorMessage="mfaErrorMessage"
            submitText="Verify & Continue"
            @verify="verifyMFACode"
            @resend="resendMFACode"
            @cancel="cancelMFA"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
let apiHost = process.env.VUE_APP_API_HOST;
import authRequired from '@/components/mixins/auth-required';
import { mapState } from 'vuex';

import { validationMixin } from 'vuelidate';
import { required, minLength, sameAs } from 'vuelidate/lib/validators';
import MFAVerification from '@/components/cwkit/MFAVerification';

export default {
  name: 'cw-change-password',
  // share common functionality with component mixins
  mixins: [authRequired, validationMixin],
  components: {
    'cw-mfa-verification': MFAVerification,
  },
  props: {},
  // variables
  data() {
    return {
      currentPassword: '',
      password: '',
      confirmPassword: '',
      // MFA re-authentication state
      showMFAPrompt: false,
      mfaCode: '',
      mfaError: false,
      mfaErrorMessage: '',
      mfaResolver: null,
      mfaVerificationId: null,
      mfaPhoneNumber: null,
    };
  },
  validations: {
    currentPassword: {
      required,
    },
    password: {
      required,
      minLength: minLength(6),
    },
    confirmPassword: {
      required,
      sameAsPassword: sameAs('password'),
    },
  },
  computed: {
    currentPasswordError() {
      let requiredErr =
        this.$v.currentPassword.$dirty && !this.$v.currentPassword.required;
      if (requiredErr) {
        return 'Current password is required.';
      } else {
        return null;
      }
    },
    passwordError() {
      let requiredErr = this.$v.password.$dirty && !this.$v.password.required;
      let lengthErr = this.$v.password.$dirty && !this.$v.password.minLength;

      if (requiredErr) {
        return 'A password is required.';
      } else if (lengthErr) {
        return 'Must be at least 6 characters.';
      } else {
        return null;
      }
    },
    confirmPasswordError() {
      let requiredErr =
        this.$v.confirmPassword.$dirty && !this.$v.confirmPassword.required;
      let matchErr =
        this.$v.confirmPassword.$dirty &&
        !this.$v.confirmPassword.sameAsPassword;

      if (requiredErr) {
        return 'Password confirmation is required.';
      } else if (matchErr) {
        return 'Passwords do not match.';
      } else {
        return null;
      }
    },
    ...mapState({
      token: state => state.user.token,
    }),
  },
  // methods
  watch: {},
  methods: {
    async submit() {
      // Validate Client side
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$modal.show('alert', {
          title: 'Insufficient Info',
          message:
            'Some of the fields are invalid. Please check that you have entered the required information.',
          type: 'warning',
        });
        return;
      }

      try {
        this.$modal.show('wifi-loader', { message: 'Verifying password...' });

        // Re-authenticate with current password
        const reauthResult = await this.$store.dispatch(
          'user/reauthenticateWithPassword',
          {
            password: this.currentPassword,
          }
        );

        // Check if MFA is required
        if (reauthResult.requiresMFA) {
          this.$modal.hide('wifi-loader');

          if (reauthResult.native) {
            // Native: code already sent by signInWithMfa, verificationId returned directly
            this.mfaResolver = 'native';
            this.mfaVerificationId = reauthResult.verificationId;
            this.mfaPhoneNumber = reauthResult.phoneNumber;
            this.showMFAPrompt = true;
          } else {
            // Web: Store resolver and send code via reCAPTCHA
            this.mfaResolver = reauthResult.resolver;

            try {
              const mfaResult = await this.$store.dispatch(
                'user/sendReauthMfaCode',
                {
                  resolver: this.mfaResolver,
                }
              );

              this.mfaVerificationId = mfaResult.verificationId;
              this.mfaPhoneNumber = mfaResult.phoneNumber;
              this.showMFAPrompt = true;
            } catch (error) {
              window.console.error('Failed to send MFA code:', error);
              this.$modal.show('alert', {
                title: 'Error',
                message: 'Failed to send verification code. Please try again.',
                type: 'warning',
              });
            }
          }
          return;
        }

        // If no MFA required, proceed with password change
        await this.changePassword();
      } catch (err) {
        this.$modal.hide('wifi-loader');
        window.console.error('Re-authentication error:', err);
        this.$modal.show('alert', {
          title: 'Authentication Failed',
          message: err.message || 'Failed to verify your current password.',
          type: 'warning',
        });
      }
    },

    async verifyMFACode(code) {
      // Handle both direct code parameter (from component) and mfaCode data property
      const verificationCode = typeof code === 'string' ? code : this.mfaCode;

      if (verificationCode.length !== 6) return;

      try {
        this.$modal.show('wifi-loader', { message: 'Verifying code...' });
        this.mfaError = false;
        this.mfaErrorMessage = '';

        // Complete MFA re-authentication
        if (this.mfaResolver === 'native') {
          // Native: complete MFA sign-in natively and update store token
          await this.$store.dispatch('user/completeNativeMfaReauth', {
            verificationId: this.mfaVerificationId,
            code: verificationCode,
          });
        } else {
          // Web: complete MFA re-authentication with resolver
          await this.$store.dispatch('user/reauthenticateWithMFA', {
            resolver: this.mfaResolver,
            verificationId: this.mfaVerificationId,
            code: verificationCode,
          });
        }

        // Now change the password
        await this.changePassword();
      } catch (error) {
        this.$modal.hide('wifi-loader');
        window.console.error('MFA verification error:', error);

        this.mfaError = true;
        this.mfaErrorMessage = 'Invalid code. Please try again.';
        this.mfaCode = '';
      }
    },

    async resendMFACode() {
      try {
        this.mfaError = false;
        this.mfaErrorMessage = '';
        this.mfaCode = '';
        this.$modal.show('wifi-loader', { message: 'Resending code...' });

        let mfaResult;
        if (this.mfaResolver === 'native') {
          // Native: resend code via native plugin
          mfaResult = await this.$store.dispatch('user/resendMfaCode');
        } else {
          // Web: resend code via JS SDK with resolver
          mfaResult = await this.$store.dispatch('user/sendReauthMfaCode', {
            resolver: this.mfaResolver,
          });
        }

        this.mfaVerificationId = mfaResult.verificationId;
        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', {
          title: 'Code Resent',
          message: 'A new verification code has been sent to your phone.',
          buttons: {
            closeTitle: 'Ok',
          },
        });
      } catch (error) {
        window.console.error('Error resending MFA code:', error);
        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', {
          title: 'Error',
          message: 'Failed to resend verification code. Please try again.',
          type: 'warning',
        });
      }
    },

    async changePassword() {
      try {
        this.$modal.show('wifi-loader', { message: 'Changing password...' });
        await this.$store.dispatch('user/refreshToken');

        let response = await axios.post(
          `${apiHost}/auth/change-password`,
          {
            currentPassword: this.currentPassword,
            password: this.password,
          },
          {
            headers: {
              Authorization: this.token,
            },
          }
        );

        this.password = '';
        this.confirmPassword = '';
        this.currentPassword = '';
        this.showMFAPrompt = false;
        this.mfaCode = '';
        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', response.data);
        this.$modal.hide('ChangePassword');
      } catch (err) {
        this.$modal.hide('wifi-loader');
        let res = err.response;
        if (res && res.data) {
          this.$modal.show('alert', {
            title: res.data.title,
            message: res.data.message,
            type: 'warning',
          });
        }
      }
    },

    cancelMFA() {
      this.showMFAPrompt = false;
      this.mfaCode = '';
      this.mfaError = false;
      this.mfaErrorMessage = '';
      this.mfaResolver = null;
      this.mfaVerificationId = null;
      this.mfaPhoneNumber = null;
    },
  },
};
</script>

<style lang="stylus" scoped></style>
